import type { GroupKey, Todo } from "@common/types/todo";
import { Button } from "@web/src/app/components/button";
import { TODO_ITEM_HEIGHT, TODO_ITEM_WIDTH } from "@web/src/app/consts/view-consts";
import type { AddTodoButtonDroppableData, AllDndTypeBundle } from "@web/src/app/use-cases/drag-and-drop";
import { makeDropHandlers } from "@web/src/app/use-cases/drag-and-drop";
import type { DroppableId } from "@web/src/app/use-cases/id-flavors";
import type { HighlightableId } from "@web/src/app/use-cases/todo-highlight";
import { todoIdToHighlightId } from "@web/src/app/use-cases/todo-highlight";
import { WebEvents } from "@web/src/app/web-events";
import { useDroppable } from "@web/src/hooks/dnd/useDroppable";
import { useHighlightable } from "@web/src/hooks/useHighlightable";
import { useStores } from "@web/src/hooks/useStore";
import type { AddTodoButtonHighlightableObject } from "@web/src/store/highlight-store";
import classNames from "classnames";
import { observer } from "mobx-react";
import type { FC } from "react";
import React, { useRef } from "react";
import { flushSync } from "react-dom";
import { PiPlusBold } from "react-icons/pi";

interface AddTodoButtonProps {
  groupKey: GroupKey;
  isSameGroupAsIsolated: boolean;
  tiny?: boolean;
}

const AddTodoButton: FC<AddTodoButtonProps> = observer(({ groupKey, isSameGroupAsIsolated, tiny }) => {
  const { dndStore, spaceStore } = useStores();
  const todoStore = spaceStore.currentTodoStore;

  const buttonRef = useRef<HTMLDivElement>(null);

  const onAddTodo = React.useCallback(() => {
    let newTodo: Todo | undefined;
    flushSync(() => {
      newTodo = todoStore.addTodoLast(groupKey);
    });
    newTodo && WebEvents.emit("WEB_FOCUS_TODO", todoIdToHighlightId(newTodo.id, groupKey));
  }, [groupKey, todoStore]);

  const highlightable = React.useMemo<AddTodoButtonHighlightableObject>(
    () => ({
      kind: "add-todo-button",
      id: addTodoHighlightableId(groupKey),
      ref: buttonRef,
      onEnter: onAddTodo,
      data: { groupKey },
    }),
    [groupKey, onAddTodo],
  );

  const { isHighlighted } = useHighlightable(highlightable);

  const droppableData = React.useMemo<AddTodoButtonDroppableData>(
    () => ({
      droppableId: addTodoDroppableId(groupKey),
      kind: "add_todo_button",
      groupKey: groupKey,
    }),
    [groupKey],
  );

  const { containerProps: droppableContainerProps } = useDroppable<AllDndTypeBundle>(
    addTodoDroppableId(groupKey),
    droppableData,
    buttonRef,
    dndStore,
  );

  return (
    <Button
      {...droppableContainerProps}
      ref={buttonRef}
      className={classNames(
        "flex flex-row text-glyph-scd items-center justify-center transition-200 rounded-lg py-0 hover:bg-surface-scd whitespace-nowrap",
        {
          "bg-surface-cst text-glyph-cst-prm hover:bg-surface-cst-scd shadow-1": isHighlighted,
          foreground: isSameGroupAsIsolated || isHighlighted,
          "foreground-secondary": isSameGroupAsIsolated && !isHighlighted,
          "px-7 text-sm gap-2": !tiny,
          "text-xs gap-1": tiny,
        },
      )}
      onClick={onAddTodo}
      style={{
        height: TODO_ITEM_HEIGHT(tiny),
        transform: isHighlighted ? "" : `scale(0.94)`,
        borderRadius: undefined,
        ...(!tiny && {
          marginTop: 8,
          maxWidth: TODO_ITEM_WIDTH,
          minWidth: (TODO_ITEM_WIDTH * 5) / 6,
        }),
        ...(tiny && {
          width: "100%",
        }),
      }}
      leadingIcon={<PiPlusBold className="w-3 h-3" />}
    >
      {!tiny ? "Create Todo" : "Add Todo"}
    </Button>
  );
});

export default AddTodoButton;

export function addTodoDroppableId(groupKey: GroupKey): DroppableId {
  return `${groupKey}_add_todo_button`;
}
export function addTodoHighlightableId(groupKey: GroupKey): HighlightableId {
  return `add_todo:${groupKey}`;
}

export function groupKeyFromDroppableId(droppableId: DroppableId): GroupKey {
  return droppableId.replace("_add_todo_button", "");
}
